import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import Swiper from 'swiper';
import {
  A11y,
  Autoplay,
  Navigation,
  Pagination,
} from 'swiper/modules';

interface Slide {
  title: string;
  subtitle?: string;
  body?: string;
  gradient: string;
  imageUrl?: string;
  ctaText?: string;
  ctaHref?: string;
}

@Component({
  selector: 'app-hero',
  standalone: true,
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.scss'],
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule],
})
export class HeroComponent implements AfterViewInit, OnDestroy {
  @ViewChild('swiperRoot', { static: true })
  private swiperRoot!: ElementRef<HTMLElement>;

  @ViewChild('paginationEl', { static: true })
  private paginationEl!: ElementRef<HTMLElement>;

  @ViewChild('prevEl', { static: true })
  private prevEl!: ElementRef<HTMLElement>;

  @ViewChild('nextEl', { static: true })
  private nextEl!: ElementRef<HTMLElement>;

  slides: Slide[] = [
    {
      title: 'Welcome to artman.io',
      subtitle: 'Sr Software Engineer - Jr Farmer - Gamer - Creator',
      body: 'Here you can find my projects, links to my open source work, and more about me.',
      gradient:
        'linear-gradient(135deg, rgba(0, 77, 122, 0.82), rgba(0, 135, 147, 0.48), rgba(168, 235, 18, 0.2))',
      imageUrl: '/assets/bg.jpg',
      ctaText: 'About Me',
      ctaHref: '/about',
    },
    {
      title: 'National Parks Service',
      subtitle: 'Fuzz Searching / Virtual Scrolling / API Integrations',
      body: 'The national parks service app is a demo of a material app that integrates with multiple NPS APIs to provide a rich user experience.',
      gradient:
        'linear-gradient(140deg, rgba(0, 34, 58, 0.84), rgba(0, 77, 122, 0.58), rgba(0, 135, 147, 0.28))',
      ctaText: 'View Project',
      ctaHref: '/nps',
    },
    {
      title: 'Political Donor Insights',
      subtitle: 'OpenFEC analytics / Recipient search / Contribution drill-down',
      body: 'Inspect campaign fundraising totals, isolate donor slices, and review the largest reported contributions in a responsive dashboard.',
      gradient:
        'linear-gradient(135deg, rgba(12, 34, 58, 0.9), rgba(0, 77, 122, 0.64), rgba(0, 135, 147, 0.38))',
      ctaText: 'Explore Dashboard',
      ctaHref: '/political-donor-dashboard',
    },
    {
      title: 'Data you can actually trust.',
      subtitle: 'Reporting, performance, and ops visibility',
      body: 'I care about correctness, performance, and making complex data usable.',
      gradient:
        'linear-gradient(125deg, rgba(0, 52, 89, 0.86), rgba(0, 135, 147, 0.46), rgba(168, 235, 18, 0.3))',
    },
  ];

  private swiper?: Swiper;

  ngAfterViewInit(): void {
    this.swiper = new Swiper(this.swiperRoot.nativeElement, {
      modules: [Navigation, Pagination, Autoplay, A11y],
      speed: 900,
      loop: this.slides.length > 1,
      autoplay: {
        delay: 15000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      navigation: {
        prevEl: this.prevEl.nativeElement,
        nextEl: this.nextEl.nativeElement,
      },
      pagination: {
        el: this.paginationEl.nativeElement,
        clickable: true,
      },
      a11y: {
        enabled: true,
      },
    });
  }

  ngOnDestroy(): void {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = undefined;
    }
  }

  previousSlide(): void {
    this.swiper?.slidePrev();
  }

  nextSlide(): void {
    this.swiper?.slideNext();
  }

  isInternalLink(link?: string): boolean {
    return !!link && link.startsWith('/');
  }

  slideBackground(slide: Slide): string {
    if (slide.imageUrl) {
      return `${slide.gradient}, url(${slide.imageUrl})`;
    }

    return `${slide.gradient}, url('/assets/bg.jpg')`;
  }
}
